import { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  FaArrowLeft,
  FaCalendar,
  FaTrophy,
  FaCertificate,
  FaBullhorn,
  FaEye,
  FaMapMarkerAlt,
  FaBuilding,
  FaShare,
  FaFacebook,
  FaTwitter,
  FaLinkedin,
  FaWhatsapp,
  FaUser,
  FaSpinner,
  FaNewspaper,
} from 'react-icons/fa';
import { newsAPI } from '../../config/api';

const NewsDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [news, setNews] = useState(null);
  const [relatedNews, setRelatedNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchNewsDetail = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      console.log('🔵 Fetching news with slug:', slug);

      // Fetch the news by slug
      const response = await newsAPI.getBySlug(slug);
      console.log('✅ News data received:', response.data);

      // Check if news is published
      if (response.data.status !== 'published') {
        setError('This news article is not available');
        setLoading(false);
        return;
      }

      setNews(response.data);

      // Fetch all news to get related ones
      const allNewsResponse = await newsAPI.getAll();
      console.log('✅ All news received:', allNewsResponse.data);

      const allNews = Array.isArray(allNewsResponse.data)
        ? allNewsResponse.data.filter(n => n.status === 'published')
        : [];

      const related = allNews
        .filter(
          (n) => n.slug !== slug && n.category === response.data.category
        )
        .slice(0, 3);

      console.log('✅ Related news:', related);
      setRelatedNews(related);
    } catch (err) {
      console.error('❌ Error fetching news:', err);
      setError(
        err.response?.data?.error ||
          'Failed to load news. It may not exist or there was a server error.'
      );
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => {
    fetchNewsDetail();
    window.scrollTo(0, 0);
  }, [fetchNewsDetail]);

  // Get full image URL
  const getImageUrl = (imagePath) => {
    if (!imagePath) return null;
    if (imagePath.startsWith('http')) return imagePath;
    return `https://worganaturals.com${imagePath}`;
  };

  const getIcon = (category) => {
    const icons = {
      Achievement: FaTrophy,
      Certification: FaCertificate,
      Announcement: FaBullhorn,
      Partnership: FaBuilding,
    };
    return icons[category] || FaNewspaper;
  };

  const getCategoryColor = (category) => {
    const colors = {
      Achievement: 'from-yellow-400 to-orange-500',
      Certification: 'from-green-400 to-emerald-600',
      Announcement: 'from-blue-400 to-indigo-600',
      Partnership: 'from-purple-400 to-pink-600',
      default: 'from-gray-400 to-gray-600',
    };
    return colors[category] || colors.default;
  };

  const getCategoryBadgeColor = (category) => {
    const colors = {
      Achievement: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
      Certification: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
      Announcement: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
      Partnership: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200',
      default: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200',
    };
    return colors[category] || colors.default;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown date';
    try {
      const options = { year: 'numeric', month: 'long', day: 'numeric' };
      return new Date(dateString).toLocaleDateString('en-US', options);
    } catch (error) {
      return 'Unknown date';
    }
  };

  const shareUrl = typeof window !== 'undefined' ? window.location.href : '';
  const shareTitle = news?.title || '';

  const shareLinks = {
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`,
    twitter: `https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareTitle)}`,
    linkedin: `https://www.linkedin.com/shareArticle?mini=true&url=${encodeURIComponent(shareUrl)}&title=${encodeURIComponent(shareTitle)}`,
    whatsapp: `https://wa.me/?text=${encodeURIComponent(shareTitle + ' ' + shareUrl)}`,
  };

  const handleShare = (platform) => {
    window.open(shareLinks[platform], '_blank', 'width=600,height=400');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-green-50 to-blue-50 dark:from-dark-900 dark:to-dark-800">
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center"
        >
          <FaSpinner className="animate-spin text-6xl text-green-600 dark:text-green-400 mx-auto mb-4" />
          <p className="text-xl text-gray-600 dark:text-gray-300">
            Loading news...
          </p>
        </motion.div>
      </div>
    );
  }

  if (error || !news) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-green-50 to-blue-50 dark:from-dark-900 dark:to-dark-800 px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center bg-white dark:bg-dark-700 p-8 rounded-2xl shadow-xl max-w-md"
        >
          <div className="text-red-500 text-6xl mb-4">⚠️</div>
          <h2 className="text-2xl font-bold text-gray-800 dark:text-white mb-2">
            News Not Found
          </h2>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            {error || 'The news article you are looking for does not exist.'}
          </p>
          <button
            onClick={() => navigate('/about/news')}
            className="bg-green-600 text-white px-6 py-3 rounded-lg hover:bg-green-700 transition font-semibold"
          >
            Back to News
          </button>
        </motion.div>
      </div>
    );
  }

  const Icon = getIcon(news.category);

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-blue-50 to-green-100 dark:from-dark-900 dark:via-dark-800 dark:to-dark-900">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="pt-32 pb-12 px-6 md:px-12 lg:px-20"
      >
        <div className="container mx-auto max-w-5xl">
          <Link
            to="/about/news"
            className="inline-flex items-center gap-2 text-gray-700 dark:text-gray-300 hover:text-green-600 dark:hover:text-green-400 transition mb-8 group"
          >
            <FaArrowLeft className="group-hover:-translate-x-1 transition-transform" />
            <span className="font-semibold">Back to News</span>
          </Link>

          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.2 }}
          >
            {news.category && (
              <span
                className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold shadow-lg mb-6 ${getCategoryBadgeColor(
                  news.category
                )}`}
              >
                <Icon />
                {news.category}
              </span>
            )}

            <h1 className="text-4xl md:text-6xl font-bold mb-6 text-gray-900 dark:text-white leading-tight">
              {news.title}
            </h1>

            <div className="flex flex-wrap gap-6 text-sm text-gray-600 dark:text-gray-400">
              {news.author && (
                <div className="flex items-center gap-2">
                  <FaUser className="text-green-600 dark:text-green-400" />
                  <span>{news.author}</span>
                </div>
              )}
              {news.published_date && (
                <div className="flex items-center gap-2">
                  <FaCalendar className="text-green-600 dark:text-green-400" />
                  <span>{formatDate(news.published_date)}</span>
                </div>
              )}
              {news.views !== undefined && (
                <div className="flex items-center gap-2">
                  <FaEye className="text-green-600 dark:text-green-400" />
                  <span>{news.views} views</span>
                </div>
              )}
              {news.location && (
                <div className="flex items-center gap-2">
                  <FaMapMarkerAlt className="text-green-600 dark:text-green-400" />
                  <span>{news.location}</span>
                </div>
              )}
              {news.organization && (
                <div className="flex items-center gap-2">
                  <FaBuilding className="text-green-600 dark:text-green-400" />
                  <span>{news.organization}</span>
                </div>
              )}
            </div>
          </motion.div>
        </div>
      </motion.div>

      {/* Article Content */}
      <div className="px-6 md:px-12 lg:px-20 pb-20">
        <div className="container mx-auto max-w-5xl">
          <motion.article
            initial={{ y: 30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="backdrop-blur-sm bg-white/90 dark:bg-dark-700/90 rounded-3xl shadow-2xl overflow-hidden"
          >
            {/* Featured Image */}
            {news.image ? (
              <div className="relative h-96 md:h-[500px] overflow-hidden">
                <img
                  src={getImageUrl(news.image)}
                  alt={news.image_alt || news.title}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    const parent = e.target.parentElement;
                    if (parent) {
                      parent.innerHTML = `
                        <div class="w-full h-full bg-gradient-to-br ${getCategoryColor(
                          news.category
                        )} flex items-center justify-center">
                          <svg class="text-white text-9xl opacity-30" fill="currentColor" viewBox="0 0 20 20" style="width: 144px; height: 144px;">
                            <path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z" />
                            <path fill-rule="evenodd" d="M4 5a2 2 0 012-2 3 3 0 003 3h2a3 3 0 003-3 2 2 0 012 2v11a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm3 4a1 1 0 000 2h.01a1 1 0 100-2H7zm3 0a1 1 0 000 2h3a1 1 0 100-2h-3zm-3 4a1 1 0 100 2h.01a1 1 0 100-2H7zm3 0a1 1 0 100 2h3a1 1 0 100-2h-3z" clip-rule="evenodd" />
                          </svg>
                        </div>
                      `;
                    }
                  }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
              </div>
            ) : (
              <div
                className={`w-full h-96 md:h-[500px] bg-gradient-to-br ${getCategoryColor(
                  news.category
                )} flex items-center justify-center`}
              >
                <Icon className="text-white text-9xl opacity-30" />
              </div>
            )}

            {/* Content */}
            <div className="p-8 md:p-12 lg:p-16">
              {/* Excerpt */}
              {news.excerpt && (
                <div className="bg-green-50 dark:bg-green-900/30 border-l-4 border-green-600 dark:border-green-400 p-6 mb-8 rounded-r-xl">
                  <p className="text-lg text-gray-700 dark:text-gray-300 italic leading-relaxed">
                    {news.excerpt}
                  </p>
                </div>
              )}

              {/* Main Content */}
              <div className="prose prose-lg dark:prose-invert max-w-none prose-headings:text-gray-900 dark:prose-headings:text-white prose-p:text-gray-700 dark:prose-p:text-gray-300 prose-a:text-green-600 dark:prose-a:text-green-400 prose-strong:text-gray-900 dark:prose-strong:text-white">
                <div
                  className="text-gray-700 dark:text-gray-300 leading-relaxed"
                  dangerouslySetInnerHTML={{ __html: news.content }}
                />
              </div>

              {/* Share Section */}
              <div className="mt-12 pt-8 border-t border-gray-200 dark:border-gray-600">
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                  <h3 className="text-xl font-bold text-gray-800 dark:text-white flex items-center gap-2">
                    <FaShare className="text-green-600 dark:text-green-400" />
                    Share this news
                  </h3>
                  <div className="flex gap-3">
                    <motion.button
                      whileHover={{ scale: 1.1, y: -2 }}
                      whileTap={{ scale: 0.9 }}
                      onClick={() => handleShare('facebook')}
                      className="w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center hover:bg-blue-700 transition shadow-lg"
                      title="Share on Facebook"
                    >
                      <FaFacebook className="text-xl" />
                    </motion.button>
                    <motion.button
                      whileHover={{ scale: 1.1, y: -2 }}
                      whileTap={{ scale: 0.9 }}
                      onClick={() => handleShare('twitter')}
                      className="w-12 h-12 bg-sky-500 text-white rounded-full flex items-center justify-center hover:bg-sky-600 transition shadow-lg"
                      title="Share on Twitter"
                    >
                      <FaTwitter className="text-xl" />
                    </motion.button>
                    <motion.button
                      whileHover={{ scale: 1.1, y: -2 }}
                      whileTap={{ scale: 0.9 }}
                      onClick={() => handleShare('linkedin')}
                      className="w-12 h-12 bg-blue-700 text-white rounded-full flex items-center justify-center hover:bg-blue-800 transition shadow-lg"
                      title="Share on LinkedIn"
                    >
                      <FaLinkedin className="text-xl" />
                    </motion.button>
                    <motion.button
                      whileHover={{ scale: 1.1, y: -2 }}
                      whileTap={{ scale: 0.9 }}
                      onClick={() => handleShare('whatsapp')}
                      className="w-12 h-12 bg-green-500 text-white rounded-full flex items-center justify-center hover:bg-green-600 transition shadow-lg"
                      title="Share on WhatsApp"
                    >
                      <FaWhatsapp className="text-xl" />
                    </motion.button>
                  </div>
                </div>
              </div>
            </div>
          </motion.article>

          {/* Related News */}
          {relatedNews.length > 0 && (
            <motion.section
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="mt-16"
            >
              <h2 className="text-3xl font-bold text-gray-800 dark:text-white mb-8">
                Related News
              </h2>
              <div className="grid md:grid-cols-3 gap-6">
                {relatedNews.map((item) => {
                  const RelatedIcon = getIcon(item.category);
                  return (
                    <Link
                      key={item.id}
                      to={`/news/${item.slug}`}
                      className="bg-white dark:bg-dark-700 rounded-xl shadow-lg overflow-hidden hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 group"
                    >
                      <div className={`h-48 bg-gradient-to-br ${getCategoryColor(item.category)} relative overflow-hidden`}>
                        {item.image ? (
                          <img
                            src={getImageUrl(item.image)}
                            alt={item.image_alt || item.title}
                            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                            onError={(e) => {
                              e.target.style.display = 'none';
                            }}
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <RelatedIcon className="text-white text-5xl opacity-50" />
                          </div>
                        )}
                        {item.category && (
                          <div className="absolute top-3 left-3">
                            <span
                              className={`px-3 py-1 rounded-full text-xs font-semibold ${getCategoryBadgeColor(
                                item.category
                              )}`}
                            >
                              {item.category}
                            </span>
                          </div>
                        )}
                      </div>
                      <div className="p-6">
                        <h3 className="font-bold text-lg text-gray-800 dark:text-white mb-2 line-clamp-2 group-hover:text-green-600 dark:group-hover:text-green-400 transition">
                          {item.title}
                        </h3>
                        <p className="text-gray-600 dark:text-gray-400 text-sm line-clamp-2 mb-3">
                          {item.excerpt ||
                            (item.content
                              ? item.content.substring(0, 100) + '...'
                              : '')}
                        </p>
                        <div className="flex items-center gap-3 text-xs text-gray-500 dark:text-gray-400">
                          <span className="flex items-center gap-1">
                            <FaCalendar />
                            {formatDate(item.published_date || item.created_at)}
                          </span>
                          <span className="flex items-center gap-1">
                            <FaEye />
                            {item.views || 0}
                          </span>
                        </div>
                      </div>
                    </Link>
                  );
                })}
              </div>
            </motion.section>
          )}
        </div>
      </div>
    </div>
  );
};

export default NewsDetail;
